import { Action, configureStore, getDefaultMiddleware, ThunkAction } from '@reduxjs/toolkit';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import Storage from '~/utils/Storage';
import * as rootReducer from './reducers';

export const history = createBrowserHistory();

const persistedState = Storage.getItem(process.env.REACT_APP_FIRESTORE_DB_ENV_VERSION_NAME) ? JSON.parse(Storage.getItem(process.env.REACT_APP_FIRESTORE_DB_ENV_VERSION_NAME) || '{}') : {};

export const store = configureStore({
    reducer: {
        router: connectRouter(history),
        persistedState,
        ...rootReducer
    },
    preloadedState: persistedState,
    middleware: [routerMiddleware(history), ...getDefaultMiddleware({ serializableCheck: false })],
    devTools: process.env.NODE_ENV !== 'production'
});

// Slices that must never reach localStorage.
//
// Everything else is persisted wholesale by the subscriber below, which is how
// this app rehydrates. That default is wrong for anything that grants access:
// a persisted adminMfa saying `status: 'valid'` would be believed on the next
// page load and the gate in PrivateRoute would let the member straight through
// until the server got around to contradicting it. The admin session token
// itself never comes near redux -- it lives in the in-memory configStore -- but
// the *verdict* must not be durable either.
const PERSIST_BLACKLIST = [
    'adminMfa'
];

store.subscribe(() => {
    const state = store.getState() as Record<string, unknown>;

    const persistable = Object.keys(state).reduce((acc: Record<string, unknown>, key) => {
        if (PERSIST_BLACKLIST.includes(key)) return acc;

        acc[key] = state[key];

        return acc;
    }, {});

    Storage.setItem(process.env.REACT_APP_FIRESTORE_DB_ENV_VERSION_NAME, JSON.stringify(persistable));
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
>;
